import {Vector as VectorLayer} from 'ol/layer';
import GeoJSON from 'ol/format/GeoJSON';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import {Icon, Text, Style, Fill} from 'ol/style';
import Graticule from 'ol/layer/Graticule.js';
import Stroke from 'ol/style/Stroke.js';

import {getRouteDetails, getRouteGeoJson} from '@/api/route-details/route-details.api';
import DISTINCT_COLORS from './distinct-colors';
import locationIconUrl from '@/assets/images/location.png';

const crypto = require('crypto');

// Abstract class
class UserLayer {
  _id;
  get id() {
    return this._id;
  }
  get type() {
    throw new Error('Method type must be defined');
  }
  get olLayer() {
    throw new Error('Method olLayer must be defined');
  }
  get olLayers() {
    const l = this.olLayer;
    return [l];
  }
  #setId() {
    throw new Error('Method #setId must be implemented');
  }
  constructor() {
    if (this.constructor === UserLayer) {
      throw new TypeError('Cannot instantiate abstract class');
    }
  }
}

class GridLayer extends UserLayer {
  gridType;

  get type() {
    return 'grid';
  }

  get olLayer() {
    if (this.gridType === '1') {
      const newLayer = new Graticule({
        // the style to use for the lines, optional.
        strokeStyle: new Stroke({
          color: 'rgba(255,120,0,0.9)',
          width: 1.5,
          lineDash: [1, 4],
        }),
        showLabels: true,
        wrapX: false,
      });
      return newLayer;
    } else if (this.gridType === '2') {
      const newLayer = new Graticule({
        // the style to use for the lines, optional.
        strokeStyle: new Stroke({
          color: 'black',
          width: 1.5,
          lineDash: [1, 4],
        }),
        showLabels: true,
        wrapX: false,
      });
      return newLayer;
    }
  }
  #setId() {
    return 'grid-layer-' + this.gridType;
    // throw new Error('Method #setId must be implemented');
  }
  constructor(gridType = '',
  ) {
    super();
    this.gridType = gridType;
    this.#setId();
  }
}

class UserMarkerLayer extends UserLayer {
  coordinates;
  iconUrl;
  name;
  get type() {
    return 'marker';
  }
  #setId() {
    const routeType = 'marker';
    const idCoordinates = this.coordinates.join('-');
    const sha = crypto.createHash('sha1');
    sha.update(routeType + idCoordinates + this.name);
    this._id = sha.digest('base64');
  }
  get olLayer() {
    const iconFeature = new Feature({
      geometry: new Point(this.coordinates),
      name: this.name,
    });
    const text = new Text({
      text: !!this.name ? this.name : '',
      font: 'bold 12px sans-serif',
      textBaseLine: 'top',
      scale: 2,
      offsetY: 20,
      backgroundFill: new Fill({
        color: '#fff',
      }),
    });
    const iconStyle = new Style({
      image: new Icon({
        anchorOrigin: 'bottom-left',
        anchor: [0.5, 0],
        src: !!this.iconUrl ? this.iconUrl : locationIconUrl,
        scale: 0.4,
      }),
      text: !!this.name ? text : undefined,
    });
    iconFeature.setStyle(iconStyle);
    const source = new VectorSource({
      features: [iconFeature],
    });
    const properties = {
      id: this.id,
      type: this.type,
    };
    const layer = new VectorLayer({
      source,
      properties,
    });
    return layer;
  }
  constructor(coordinates = [],
    name = '',
    iconUrl = '',
  ) {
    super();
    this.coordinates = coordinates;
    this.iconUrl = iconUrl;
    this.name = name;
    this.#setId();
  }
}
class CalculatedRouteLayer extends UserLayer {
  points;
  shortestRoute;
  routeInfo;
  routeGeoJson;
  extent;
  colorIndex;
  #setId() {
    const routeType = 'route';
    const idPoints = this.points.map((p) => p.id).join('');
    const shortestRouteDesc = this.shortestRoute ? 'shortest' : 'fastest';
    const sha = crypto.createHash('sha1');
    sha.update(routeType + idPoints + shortestRouteDesc);
    this._id = sha.digest('base64');
  }
  get description() {
    const shortestRouteDesc = this.shortestRoute ? ' (CORTO)' : ' (RÁPIDO)';
    const pointsNames = this.points.map((p) => p.name).join(' \u2192 ');
    return pointsNames + shortestRouteDesc;
  }
  get routeDescription() {
    return this.shortestRoute ? 'RUTA CORTA' : 'RUTA RÁPIDA';
  }
  get pointsEnumeration() {
    return this.points.map((p) => p.name).join(' - ');
  }
  get type() {
    return 'route';
  }
  get colorIndex() {
    return colorIndex;
  }
  get olLayers() {
    const properties = {
      id: this.id,
      type: this.type,
    };
    const blackLineStyle = new Style({
      stroke: new Stroke({
        color: 'black',
        width: 14,
      }),
    });
    const RouteLayerBlack = new VectorLayer({
      source: new VectorSource({
        format: new GeoJSON(),
      }),
      style: blackLineStyle,
      properties,
    });
    const whiteLineStyle = new Style({
      stroke: new Stroke({
        color: 'white',
        width: 10,
      }),
    });
    const RouteLayerWhite = new VectorLayer({
      source: new VectorSource({
        format: new GeoJSON(),
      }),
      style: whiteLineStyle,
      properties,
    });
    const color = DISTINCT_COLORS[this.colorIndex];
    const distinctColorLineStyle = new Style({
      stroke: new Stroke({
        // color: 'yellow',
        color,
        width: 6,
      }),
    });
    const RouteLayerDistinctColor = new VectorLayer({
      source: new VectorSource({
        format: new GeoJSON(),
      }),
      style: distinctColorLineStyle,
      properties,
    });
    const featuresGeoJson = new GeoJSON().readFeatures(this.routeGeoJson, {
      dataProjection: 'EPSG:3857',
      featureProjection: 'EPSG:3857',
    });
    RouteLayerBlack.getSource().addFeatures(featuresGeoJson);
    RouteLayerWhite.getSource().addFeatures(featuresGeoJson);
    RouteLayerDistinctColor.getSource().addFeatures(featuresGeoJson);
    return [RouteLayerBlack, RouteLayerWhite, RouteLayerDistinctColor];
  }
  setRouteInfo(routeInfo) {
    if (!this.routeInfo) {
      this.routeInfo = routeInfo;
    }
  }
  constructor({points, shortestRoute, routeInfo,
    routeGeoJson, extent, colorIndex}) {
    super();
    this.points = points; // array of { id, name }
    this.shortestRoute = shortestRoute; // true false
    this.routeInfo = routeInfo; // Info Object
    this.routeGeoJson = routeGeoJson;
    this.extent = extent;
    this.colorIndex = colorIndex;
    this.#setId();
  }
}

// #region Actions
const actions = {
  async addRouteLayer({state, commit}, {points, shortestRoute}) {
    const pointsIds = points.map((t) => t.id);
    const p = getRouteDetails(pointsIds, shortestRoute).
      then(async (routeInfo) => {
        const {feature, extent} = await getRouteGeoJson(pointsIds, shortestRoute);
        return {routeInfo, feature, extent};
      });
    const {routeInfo, feature: routeGeoJson, extent} = await p;
    const colorIndex = ((state.routeLayers.length + 1) % DISTINCT_COLORS.length) - 1;
    const layer = new CalculatedRouteLayer(
      {points, shortestRoute, routeInfo, routeGeoJson, extent, colorIndex},
    );
    commit('addRouteLayer', {layer});
  },
  async setRouteInfoLayer({commit}, {points, shortestRoute, id}) {
    const pointsIds = points.map((t) => t.id);
    const routeInfo = await getRouteDetails(pointsIds, shortestRoute);
    commit('setRouteInfoLayer', {routeInfo, id});
  },
  async addMarkerLayer({commit}, {coordinates, name, iconUrl}) {
    const layer = new UserMarkerLayer(coordinates, name, iconUrl);
    commit('addMarkerLayer', {layer});
  },
  async addGridLayer({commit}, {gridType}) {
    const layer = new GridLayer(gridType);
    commit('addGridLayer', {layer});
  },
  async removeRouteLayer({commit}, {id}) {
    commit('removeRouteLayer', {id});
  },
  async removeMarkerLayer({commit}, {id}) {
    commit('removeMarkerLayer', {id});
  },
  async removeGridLayer({commit}, {id}) {
    commit('removeGridLayer', {id});
  },
  async cleanRouteLayers({commit}) {
    commit('cleanRouteLayers');
  },
  async cleanMarkerLayers({commit}) {
    commit('cleanMarkerLayers');
  },
  async cleanGridLayers({commit}) {
    commit('cleanGridLayers');
  },
  async cleanLayers({commit}) {
    commit('cleanLayers');
  },
};
// #endregion

// #region Getters
const getters = {
  isGridLayerActive: (state) => {
    return !!state.gridLayers?.length;
  },
  getRouteLayerById: (state) => (id) => {
    return state.routeLayers.find((layer) => layer.id === id);
  },
};
// #endregion

// #region Mutations
const mutations = {
  addRouteLayer(state, {layer}) {
    const found = state.routeLayers.find((l) => l.id === layer.id);
    if (!found) {
      state.routeLayers.push(layer);
    }
  },
  setRouteInfoLayer(state, {routeInfo, id}) {
    const found = state.routeLayers.find((l) => l.id === layer.id);
    if (!!found && !!found?.setRouteInfo) {
      found.setRouteInfo(routeInfo);
    }
  },
  addMarkerLayer(state, {layer}) {
    const found = state.markerLayers.find((l) => l.id === layer.id);
    if (!found) {
      state.markerLayers.push(layer);
    }
  },
  addGridLayer(state, {layer}) {
    const found = state.gridLayers.find((l) => l.id === layer.id);
    if (!found) {
      state.gridLayers.push(layer);
    }
  },
  removeRouteLayer(state, {id}) {
    state.routeLayers = state.routeLayers.filter((el) => el.id !== id);
  },
  removeMarkerLayer(state, {id}) {
    state.markerLayers = state.markerLayers.filter((el) => el.id !== id);
  },
  removeGridLayer(state, {id}) {
    state.gridLayers = state.gridLayers.filter((el) => el.id !== id);
  },
  cleanRouteLayers(state) {
    state.routeLayers = [];
  },
  cleanMarkerLayers(state) {
    state.markerLayers = [];
  },
  cleanGridLayers(state) {
    state.gridLayers = [];
  },
  cleanLayers(state) {
    state.routeLayers = [];
    state.markerLayers = [];
    state.gridLayers = [];
  },
};
// #endregion

// #region State
const state = () => {
  return {
    routeLayers: [],
    markerLayers: [],
    gridLayers: [],
  };
};
// #endregion


// #region Etc
const namespaced = true;
const modules = []; // Submodules
// #endregion

export default {
  actions,
  getters,
  modules,
  mutations,
  namespaced,
  state,
};
